/**
 * Design-QA route for the ფრაზები screen (mirrors /test-review and
 * /skill-tree-review): the real screen (the sticker album) with mock
 * progress, no sign-in, so Tornike can judge it on a preview link — Google
 * sign-in only returns to app.fluent.ge.
 *
 *   /phrases-review?done=N    the first N lessons of A1 are done
 *   /phrases-review?pro=no    a learner without Pro (⚡ badges)
 *
 * Tiles open /lesson-review/:id (play with nothing saved). While the real
 * phrases course is not in the bundle, a SAMPLE course stands in (labelled).
 * The real course's lessons are lazy (content/lessons.ts): the album's
 * skeleton shows while they download, as on /phrases.
 *
 * Preview hosts only — on app.fluent.ge it redirects home (isReviewHost).
 */
import { useMemo } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import { Muted, Screen } from '@fluent/ui';
import { getCourse, PHRASES_COURSE_ID } from './content/course.js';
import { getLesson } from './content/lessons.js';
import { isReviewHost } from './lib/reviewHost.js';
import { AppBottomNav } from './nav/AppBottomNav.js';
import { SAMPLE_PHRASES_TREE } from './phrases/__fixtures__/sampleTree.js';
import { usePhrasesTree } from './phrases/hooks.js';
import { buildPhrasesView, type PhraseCompletion } from './phrases/progression.js';
import { PhrasesLoading, PhrasesView } from './phrases/PhrasesView.js';
import { buildFreeAccess, isLessonGated } from './subscription/access.js';

export function PhrasesReview() {
  const navigate = useNavigate();
  const location = useLocation();
  const reviewHost = isReviewHost(window.location.hostname);
  const { tree: realTree, content } = usePhrasesTree(reviewHost);
  // The real course exists in this build but its lessons are still on the way.
  const realCourse = getCourse(PHRASES_COURSE_ID);
  const waiting = realCourse !== null && content === 'loading';
  const tree = realTree ?? SAMPLE_PHRASES_TREE;

  const params = new URLSearchParams(location.search);
  const done = Math.max(0, Number(params.get('done') ?? 0) || 0);
  const noPro = params.get('pro') === 'no';

  const view = useMemo(() => {
    const completions: Record<string, PhraseCompletion> = {};
    const a1 = tree.units[0]?.resolvedSkills.flatMap((s) => s.resolvedLessons) ?? [];
    a1.slice(0, done).forEach((lesson, i) => {
      // Every third one perfect, so the golden mark shows too.
      completions[lesson.id] = { perfect: i % 3 === 1, completedAt: i + 1 };
    });
    const access = buildFreeAccess(null, tree);
    return buildPhrasesView(tree, completions, {
      isProLocked: (id) => noPro && isLessonGated(access, id),
    });
  }, [tree, done, noPro]);

  if (!reviewHost) return <Navigate to="/" replace />;

  if (waiting) {
    return (
      <Screen>
        <PhrasesLoading levelCount={realCourse.units.length} />
        <AppBottomNav activeTo="/phrases" />
      </Screen>
    );
  }

  return (
    <Screen>
      <PhrasesView
        view={view}
        onOpen={(lesson) => {
          if (!getLesson(lesson.id)) return; // sample lessons have no content
          navigate(`/lesson-review/${lesson.id}`, { state: { from: `${location.pathname}${location.search}` } });
        }}
        notice={
          <Muted style={{ marginBottom: 'var(--space-4, 16px)' }}>
            Preview — nothing is saved. ?done=N marks the first N A1 lessons done, ?pro=no shows a learner without Pro.
            {realTree
              ? ''
              : realCourse && content === 'error'
                ? ' SAMPLE DATA: the phrases lessons did not load — reload the page.'
                : ' SAMPLE DATA: the phrases course is not written yet.'}
          </Muted>
        }
      />
      {/* The real screen's teal ფრაზები tab — the URL here is /phrases-review. */}
      <AppBottomNav activeTo="/phrases" />
    </Screen>
  );
}
